'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Context, FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ParticipantDropdown } from '@/components/ParticipantDropdown/ParticipantDropdown';
import { LeftArrowIcon, LogoIcon } from '@/components/ui/Icons/Icons';
import { PAGE_MOBILE_HEADER_ID, ROUTES } from '@/constants/common';
import { useParticipant } from '@/providers/ParticipantProvider';
import { RequiredHeaderContextProps } from '@/types';
import { checkLocalRouting } from '@/utils/helpers/checkLockRouting';

import * as S from './MobileHeader.styles';

const SearchInput = dynamic(
  () =>
    import('@/components/Header/HeaderSearchInput').then(
      mod => mod.HeaderSearchInput
    ),
  { ssr: false }
);

export interface MobileHeaderProps {
  showBack?: boolean;
  showParticipantDropdown?: boolean;
  showSearch?: boolean;
  context?: Context<RequiredHeaderContextProps>;
  title?: string;
  rightElement?: ReactNode;
  onBack?: () => void;
}

export const MobileHeader: FC<MobileHeaderProps> = ({
  showBack,
  showParticipantDropdown,
  showSearch,
  context,
  title,
  rightElement,
  onBack
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { participant } = useParticipant();
  const [isLocalRouting, setIsLocalRouting] = useState(false);

  const canGoBack = showBack && (!!onBack || isLocalRouting);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (isLocalRouting) {
      router.back();
    }
  };

  useEffect(() => {
    setIsLocalRouting(checkLocalRouting());
  }, []);

  return (
    <S.Wrapper id={PAGE_MOBILE_HEADER_ID} data-cy="mobile-header">
      <S.LeftSection>
        {canGoBack && (
          <S.BackButton onClick={handleBack} aria-label="Go back">
            <LeftArrowIcon />
          </S.BackButton>
        )}

        {title && (canGoBack || !showBack) ? (
          <S.Title>{title}</S.Title>
        ) : (
          <Link href={ROUTES.HOME} data-cy="mobile-header-logo">
            <LogoIcon />
          </Link>
        )}

        {showParticipantDropdown && <ParticipantDropdown />}
      </S.LeftSection>

      <S.RightSection>
        {showSearch && context && (
          <SearchInput
            context={context as Context<RequiredHeaderContextProps>}
            placeholder={t(`Header.search_by_${participant.toLowerCase()}`)}
          />
        )}

        {rightElement}
      </S.RightSection>
    </S.Wrapper>
  );
};
